// Lead workspace — concise header + 5 sections (Overview, AI Sales Brain, Proposal & Quotation, Follow-up, Activity).
// 2026-10-08 Phase 3b simplification:
//   • Primary action strip under header (WhatsApp · Contacted · Create Proposal · Won · Lost)
//   • Quick follow-up chips (Tomorrow / 3d / 7d / Custom)
//   • Overview always visible; AI Sales Brain / Proposal / Activity / Follow-up are
//     COLLAPSED accordions that open on demand — Kaily no longer wades through
//     advanced panels to do a one-tap action.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, MessageCircle, Instagram, Mail, MapPin, Globe, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { STAGE_LABELS, API } from "./SalesCRM";
import { MoveDialog } from "./PipelineView";
import OfferSection, { ConvertSection } from "./OfferBuilder";
import JourneyPanel from "./JourneyPanel";
import BrainSection, { Box } from "./BrainSection";
import ProposalBuilder from "./ProposalBuilder";

const FRAG_SECTION = { convert: "offer", journey: "offer", brain: "brain", offer: "offer", followup: "followup", outreach: "followup", activity: "activity" };

const waLink = (phone, text = "") => `https://wa.me/${(phone || "").replace(/\D/g, "")}${text ? `?text=${encodeURIComponent(text)}` : ""}`;

export default function LeadDetail({ leadId, onClose }) {
    const [id, frag] = leadId.split("#");
    const [lead, setLead] = useState(null);
    const [move, setMove] = useState(null);
    // Accordion open-state. Overview is always rendered — the others start
    // collapsed. A frag deep-link (?lead=XYZ#brain) auto-opens that section.
    const initialOpen = FRAG_SECTION[frag] || "";
    const [open, setOpen] = useState({
        brain: initialOpen === "brain",
        offer: initialOpen === "offer",
        followup: initialOpen === "followup",
        activity: initialOpen === "activity",
    });
    const toggle = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/crm/leads/${id}`);
            setLead(data);
        } catch { toast.error("Lead not found"); onClose(); }
    }, [id, onClose]);
    useEffect(() => { load(); }, [load]);

    const patch = async (fields) => {
        try { await axios.patch(`${API}/crm/leads/${id}`, fields); load(); }
        catch { toast.error("Save failed"); }
    };

    const quickLost = async () => {
        if (!window.confirm(`Mark ${lead?.card?.brand || "this lead"} as Lost? It will be removed from Overview and all follow-up reminders immediately. History stays searchable.`)) return;
        try {
            await axios.post(`${API}/crm/leads/${id}/lost`, {});
            toast.success("Lead marked Lost · closed");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const quickWon = async () => {
        try {
            await axios.post(`${API}/crm/leads/${id}/won`, {});
            toast.success("Lead marked Won · opening Convert to Client Project");
            setOpen((p) => ({ ...p, offer: true }));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const quickStage = async (stage, msg) => {
        try {
            await axios.post(`${API}/crm/leads/${id}/stage`, { stage });
            toast.success(msg);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };
    const quickFollowup = async (preset, date) => {
        try {
            const body = { preset };
            if (preset === "custom") body.date = date;
            await axios.post(`${API}/crm/leads/${id}/followup`, body);
            toast.success(preset === "custom" ? `Follow-up set to ${date}` : `Follow-up set · ${preset}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    };

    if (!lead) return null;
    const c = lead.card, d = lead.detail;
    const ct = d.contact || {};
    const isClosed = c.stage === "won" || c.stage === "lost" || c.stage === "spam";

    return (
        <div className="fixed inset-0 z-[60] bg-black/40 overflow-y-auto" data-testid="lead-detail">
            <div className="bg-[#FAF6EF] min-h-full w-full max-w-4xl mx-auto my-0 sm:my-6 sm:rounded-2xl p-4 sm:p-6">
                {/* HEADER */}
                <div className="flex justify-between items-start gap-3">
                    <div>
                        <h2 className="font-serif text-2xl text-cafe-espresso" data-testid="lead-detail-brand">{c.brand}</h2>
                        <div className="flex flex-wrap gap-1.5 mt-1.5 text-[10px] uppercase tracking-wide">
                            <span className="px-2 py-0.5 rounded-full bg-cafe-espresso text-white">{STAGE_LABELS[c.stage] || c.stage}</span>
                            <span className="px-2 py-0.5 rounded-full bg-neutral-200">{c.record_class}</span>
                            {c.lead_source && <span className="px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200">{c.lead_source}</span>}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <select data-testid="lead-detail-move" value="" onChange={(e) => e.target.value && setMove(e.target.value)}
                            className="border border-neutral-300 rounded-full px-2 py-1 text-[11px] bg-white">
                            <option value="">More Actions…</option>
                            {Object.keys(STAGE_LABELS).filter((s) => s !== c.stage).map((s) => <option key={s} value={s}>Move to {STAGE_LABELS[s]}</option>)}
                        </select>
                        <button data-testid="lead-detail-close" onClick={onClose} className="p-1.5 rounded-full border border-neutral-300 bg-white"><X size={15} /></button>
                    </div>
                </div>

                {/* PRIMARY ACTION STRIP — the single row Kaily uses daily.
                    Each pill is one tap, no dialogs. */}
                <div className="flex flex-wrap gap-2 mt-3" data-testid="lead-primary-actions">
                    {ct.phone && (
                        <a
                            href={waLink(ct.phone, `Hi ${c.brand} team, this is Kaily from slowcafe.my`)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="action-whatsapp"
                            className="h-9 px-4 rounded-full bg-emerald-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-emerald-700">
                            <MessageCircle size={13} /> WhatsApp
                        </a>
                    )}
                    {!isClosed && c.stage === "new_lead" && (
                        <button
                            type="button"
                            data-testid="action-contacted"
                            onClick={() => quickStage("contacted", "Marked Contacted")}
                            className="h-9 px-4 rounded-full bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700">
                            Mark Contacted
                        </button>
                    )}
                    {!isClosed && c.stage !== "proposal_sent" && c.stage !== "negotiation" && (
                        <button
                            type="button"
                            data-testid="action-proposal"
                            onClick={() => { quickStage("proposal_sent", "Moved to Proposal"); setOpen((p) => ({ ...p, offer: true })); }}
                            className="h-9 px-4 rounded-full bg-amber-600 text-white text-[12px] font-semibold hover:bg-amber-700">
                            Create Proposal
                        </button>
                    )}
                    {!isClosed && (
                        <>
                            <button
                                type="button"
                                data-testid="lead-detail-won"
                                onClick={quickWon}
                                className="h-9 px-4 rounded-full border border-emerald-200 bg-emerald-50 text-[12px] font-semibold text-emerald-700 hover:bg-emerald-100">
                                Won
                            </button>
                            <button
                                type="button"
                                data-testid="lead-detail-lost"
                                onClick={quickLost}
                                className="h-9 px-4 rounded-full border border-red-200 bg-red-50 text-[12px] font-semibold text-red-700 hover:bg-red-100">
                                Lost
                            </button>
                        </>
                    )}
                </div>

                {/* SECONDARY CONTACT PILLS */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                    {ct.instagram && <a data-testid="contact-instagram" href={ct.instagram.startsWith("http") ? ct.instagram : `https://instagram.com/${ct.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1 rounded-full bg-white border text-[11px]"><Instagram size={11} /> Instagram</a>}
                    {ct.email && <a data-testid="contact-email" href={`mailto:${ct.email}`} className="flex items-center gap-1 px-3 py-1 rounded-full bg-white border text-[11px]"><Mail size={11} /> Email</a>}
                    {ct.maps && <a data-testid="contact-maps" href={ct.maps} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1 rounded-full bg-white border text-[11px]"><MapPin size={11} /> Maps</a>}
                    {ct.website && <a data-testid="contact-website" href={ct.website.startsWith("http") ? ct.website : `https://${ct.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 px-3 py-1 rounded-full bg-white border text-[11px]"><Globe size={11} /> Website</a>}
                </div>

                {/* QUICK FOLLOW-UP CHIPS */}
                {!isClosed && (
                    <QuickFollowupBar current={c.followup_date} onPick={quickFollowup} />
                )}

                {/* OVERVIEW — always visible */}
                <div className="mt-5">
                    <OverviewSec d={d} c={c} patch={patch} onGoBrain={() => setOpen((p) => ({ ...p, brain: true }))} />
                </div>

                {/* COLLAPSIBLE SECTIONS */}
                <Accordion
                    label="AI Sales Brain"
                    hint="Optional — never required to move the lead forward"
                    testid="section-brain-toggle"
                    open={open.brain}
                    onToggle={() => toggle("brain")}
                >
                    <BrainSection id={id} reload={load} />
                </Accordion>

                <Accordion
                    label="Proposal & Quotation"
                    hint={c.stage === "won" ? "Convert this lead to a client project" : "Build the PDF, send it, track decision"}
                    testid="section-offer-toggle"
                    open={open.offer}
                    onToggle={() => toggle("offer")}
                >
                    <div className="space-y-5">
                        {(frag === "convert" || d.stage === "won") && <ConvertSection id={id} reload={load} />}
                        <OfferSection id={id} reload={load} />
                        <ProposalBuilder id={id} reload={load} />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-2">Journey · Quotation PDF · Payment</div>
                            <JourneyPanel leadId={id} onChanged={load} />
                        </div>
                    </div>
                </Accordion>

                <Accordion
                    label="Follow-up · Outreach drafts"
                    hint="Compose WhatsApp or email drafts — Kaily always sends manually"
                    testid="section-followup-toggle"
                    open={open.followup}
                    onToggle={() => toggle("followup")}
                >
                    <div className="space-y-5">
                        <FollowupSec id={id} d={d} c={c} reload={load} />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-2">Outreach drafts (Kaily always sends manually)</div>
                            <OutreachSec id={id} d={d} reload={load} waLink={waLink} />
                        </div>
                    </div>
                </Accordion>

                <Accordion
                    label="Activity"
                    hint="Timeline — every stage change and outreach sent"
                    testid="section-activity-toggle"
                    open={open.activity}
                    onToggle={() => toggle("activity")}
                >
                    <ActivitySec d={d} />
                </Accordion>
            </div>
            {move && <MoveDialog lead={c} target={move} onCancel={() => setMove(null)}
                onDone={(offerConvert) => { setMove(null); load(); if (offerConvert) setOpen((p) => ({ ...p, offer: true })); }} />}
        </div>
    );
}

function Accordion({ label, hint, testid, open, onToggle, children }) {
    return (
        <section className="mt-4 bg-white border border-border rounded-2xl overflow-hidden" data-testid={testid?.replace("-toggle", "")}>
            <button
                type="button"
                onClick={onToggle}
                data-testid={testid}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-cafe-latte/30 transition-colors">
                <div>
                    <div className="text-[13px] font-semibold text-cafe-espresso">{label}</div>
                    {hint && <div className="text-[11px] text-cafe-mocha mt-0.5">{hint}</div>}
                </div>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {open && <div className="px-4 pb-4 border-t border-border">{children}</div>}
        </section>
    );
}

function QuickFollowupBar({ current, onPick }) {
    const [customOpen, setCustomOpen] = useState(false);
    const [customDate, setCustomDate] = useState("");
    return (
        <div className="mt-4 bg-white border border-border rounded-2xl p-3" data-testid="followup-quick-bar">
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-[11px] uppercase tracking-[0.14em] text-cafe-espresso font-bold">Snooze follow-up</div>
                {current && <div className="text-[11px] text-cafe-mocha">Current: <b>{current}</b></div>}
            </div>
            <div className="flex flex-wrap gap-1.5">
                <button data-testid="followup-tomorrow" onClick={() => onPick("tomorrow")} className="h-7 px-3 rounded-full border border-border bg-cafe-cream text-[11px] font-semibold hover:border-cafe-olive">Tomorrow</button>
                <button data-testid="followup-3d" onClick={() => onPick("3d")} className="h-7 px-3 rounded-full border border-border bg-cafe-cream text-[11px] font-semibold hover:border-cafe-olive">3 days</button>
                <button data-testid="followup-7d" onClick={() => onPick("7d")} className="h-7 px-3 rounded-full border border-border bg-cafe-cream text-[11px] font-semibold hover:border-cafe-olive">7 days</button>
                {!customOpen ? (
                    <button data-testid="followup-custom" onClick={() => setCustomOpen(true)} className="h-7 px-3 rounded-full border border-border bg-cafe-cream text-[11px] font-semibold hover:border-cafe-olive">Custom…</button>
                ) : (
                    <div className="inline-flex items-center gap-1">
                        <input
                            type="date"
                            data-testid="followup-custom-date"
                            value={customDate}
                            onChange={(e) => setCustomDate(e.target.value)}
                            className="h-7 px-2 border border-border rounded-full text-[11px] bg-white"
                        />
                        <button
                            type="button"
                            data-testid="followup-custom-set"
                            disabled={!customDate}
                            onClick={() => { onPick("custom", customDate); setCustomOpen(false); setCustomDate(""); }}
                            className="h-7 px-3 rounded-full bg-cafe-espresso text-white text-[11px] font-semibold disabled:opacity-50">
                            Set
                        </button>
                        <button
                            type="button"
                            onClick={() => { setCustomOpen(false); setCustomDate(""); }}
                            className="h-7 px-2 rounded-full border border-border bg-white text-[11px]">
                            Cancel
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

                            {/* dead orphan removed 2026-10-08 */}
function OverviewSec({ d, c, patch, onGoBrain }) {
    const [notes, setNotes] = useState(d.kaily_notes || "");
    const s = ((d.opportunity || {}).cards || {}).snapshot || {};
    const snap = s.edited || s.ai || [];
    const lastMsg = (d.messages || []).slice(-1)[0];
    return (
        <div className="space-y-3" data-testid="section-overview">
            <Box title="Summary">
                {snap.length ? <ul className="text-sm space-y-1">{snap.map((b, i) => <li key={i}>· {b}</li>)}</ul>
                    : <div className="text-sm text-neutral-500">No analysis yet — <button data-testid="overview-go-brain" onClick={onGoBrain} className="underline text-cafe-olive inline-flex items-center gap-1"><Sparkles size={12} /> Analyse with AI Sales Brain</button></div>}
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-neutral-600">
                    <div>Stage: <b>{STAGE_LABELS[c.stage]}</b></div>
                    <div>Next action: <b className="text-cafe-olive">{c.next_action}</b></div>
                    <div>Follow-up: <b>{c.followup_date || "—"}</b></div>
                    <div>Last interaction: <b>{lastMsg ? `${(lastMsg.at || "").slice(0, 10)} via ${lastMsg.channel}` : "none recorded"}</b></div>
                </div>
            </Box>
            <Box title="Kaily Notes">
                <textarea data-testid="kaily-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                    className="w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-sm" placeholder="Your notes about this lead…" />
                <button data-testid="kaily-notes-save" onClick={() => { patch({ kaily_notes: notes }); toast.success("Notes saved"); }}
                    className="mt-1 text-xs px-3 py-1 rounded-full bg-cafe-espresso text-white">Save notes</button>
            </Box>
        </div>
    );
}

function OutreachSec({ id, d, reload, waLink }) {
    const [channel, setChannel] = useState("whatsapp");
    const [type, setType] = useState("first_outreach");
    const [lang, setLang] = useState("en");
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [confirmSend, setConfirmSend] = useState(null);
    const drafts = [...(d.outreach_drafts || [])].reverse().slice(0, 5);
    const msgs = [...(d.messages || [])].reverse();

    const gen = async () => {
        setBusy(true);
        try { await axios.post(`${API}/crm/leads/${id}/outreach`, { channel, type, lang }); reload(); toast.success("Draft ready"); }
        catch (err) { toast.error(err?.response?.data?.detail || "Draft failed"); }
        finally { setBusy(false); }
    };
    const saveEdit = async (draftId) => {
        await axios.patch(`${API}/crm/leads/${id}/outreach`, { draft_id: draftId, text: editText });
        setEditingId(null); reload();
    };
    return (
        <div className="space-y-3" data-testid="section-outreach">
            <Box title="Create draft (Kaily always sends manually — nothing is auto-sent)">
                <div className="flex flex-wrap gap-2">
                    <select data-testid="draft-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="border rounded-full px-2.5 py-1.5 text-xs bg-white">
                        <option value="whatsapp">WhatsApp</option><option value="instagram">Instagram DM</option><option value="email">Email</option>
                    </select>
                    <select data-testid="draft-type" value={type} onChange={(e) => setType(e.target.value)} className="border rounded-full px-2.5 py-1.5 text-xs bg-white">
                        <option value="first_outreach">First outreach</option><option value="followup">Follow-up</option><option value="re_engagement">Re-engagement</option>
                    </select>
                    <select data-testid="draft-lang" value={lang} onChange={(e) => setLang(e.target.value)} className="border rounded-full px-2.5 py-1.5 text-xs bg-white">
                        <option value="en">English</option><option value="cn">中文</option><option value="both">Bilingual</option>
                    </select>
                    <button data-testid="draft-generate-btn" disabled={busy} onClick={gen}
                        className="flex items-center gap-1 text-xs px-4 py-1.5 rounded-full bg-cafe-espresso text-white">
                        <Sparkles size={12} /> {busy ? "Writing…" : "Generate draft"}
                    </button>
                </div>
            </Box>
            {drafts.map((dr) => (
                <Box key={dr.id} title={`${dr.channel} · ${dr.type.replace(/_/g, " ")} · ${dr.lang}${dr.edited ? " · edited" : ""}`} testid={`draft-${dr.id}`}>
                    {editingId === dr.id ? (<>
                        <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={5} className="w-full border rounded-lg px-2 py-1.5 text-sm" data-testid={`draft-edit-${dr.id}`} />
                        <div className="flex gap-2 mt-1">
                            <button data-testid={`draft-save-${dr.id}`} onClick={() => saveEdit(dr.id)} className="text-xs px-3 py-1 rounded-full bg-cafe-espresso text-white">Save</button>
                            <button onClick={() => setEditingId(null)} className="text-xs px-3 py-1 rounded-full border">Cancel</button>
                        </div>
                    </>) : (<>
                        <p className="text-sm whitespace-pre-wrap">{dr.text}</p>
                        <div className="flex flex-wrap gap-2 mt-2 text-xs">
                            <button data-testid={`draft-edit-btn-${dr.id}`} onClick={() => { setEditText(dr.text); setEditingId(dr.id); }} className="px-3 py-1 rounded-full border">Edit</button>
                            <button data-testid={`draft-regen-${dr.id}`} onClick={gen} className="px-3 py-1 rounded-full border flex items-center gap-1"><RefreshCw size={11} /> Regenerate</button>
                            <button data-testid={`draft-copy-${dr.id}`} onClick={() => { navigator.clipboard.writeText(dr.text); toast.success("Copied"); }}
                                className="px-3 py-1 rounded-full border flex items-center gap-1"><Copy size={11} /> Copy</button>
                            {dr.channel === "whatsapp" && (d.contact || {}).phone &&
                                <a data-testid={`draft-open-wa-${dr.id}`} href={waLink(d.contact.phone, dr.text)} target="_blank" rel="noreferrer" className="px-3 py-1 rounded-full border">Open in WhatsApp</a>}
                            {dr.channel === "email" && (d.contact || {}).email &&
                                <a data-testid={`draft-open-email-${dr.id}`} href={`mailto:${d.contact.email}?body=${encodeURIComponent(dr.text)}`} className="px-3 py-1 rounded-full border">Open Email</a>}
                            <button data-testid={`draft-mark-contacted-${dr.id}`} onClick={() => setConfirmSend(dr)}
                                className="px-3 py-1 rounded-full bg-cafe-olive text-white flex items-center gap-1"><Check size={11} /> Mark as Contacted</button>
                        </div>
                    </>)}
                </Box>
            ))}
            <Box title="Message history" testid="message-history">
                {msgs.length ? msgs.map((m) => (
                    <div key={m.id} className="text-xs border-b last:border-0 py-2">
                        <div><b>{(m.at || "").slice(0, 16).replace("T", " ")}</b> · {m.channel} · {m.type.replace(/_/g, " ")} · <span className="text-cafe-olive">Kaily confirmed sent ✓</span></div>
                        {m.draft_excerpt && <div className="text-neutral-500 mt-0.5">"{m.draft_excerpt}…"</div>}
                        {m.response_summary && <div className="mt-0.5">Response: {m.response_summary}</div>}
                        {m.outcome && <div className="text-neutral-500">Outcome: {m.outcome}</div>}
                    </div>
                )) : <div className="text-xs text-neutral-400">No confirmed contacts yet.</div>}
            </Box>
            {confirmSend && <MarkContactedDialog id={id} draft={confirmSend} stage={d.stage}
                onClose={() => setConfirmSend(null)} onDone={() => { setConfirmSend(null); reload(); }} />}
        </div>
    );
}

function MarkContactedDialog({ id, draft, stage, onClose, onDone }) {
    const [resp, setResp] = useState("");
    const [next, setNext] = useState("Follow up on message");
    const [date, setDate] = useState(() => { const t = new Date(); t.setDate(t.getDate() + 3); return t.toISOString().slice(0, 10); });
    const [moveStage, setMoveStage] = useState(["new_lead", "qualified"].includes(stage));
    const submit = async () => {
        try {
            await axios.post(`${API}/crm/leads/${id}/messages`, {
                channel: draft.channel, type: draft.type, draft_id: draft.id, sent_confirmed: true,
                response_summary: resp, next_action: next, followup_date: date, mark_contacted: moveStage });
            toast.success("Contact recorded"); onDone();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" data-testid="mark-contacted-dialog">
            <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-2">
                <div className="text-sm font-medium">Confirm: you sent this {draft.channel} message yourself?</div>
                <div className="text-xs text-neutral-500">Only Kaily-confirmed sends are recorded. Nothing is auto-sent.</div>
                <label className="block text-xs text-neutral-600">Client response (if any)
                    <input data-testid="contacted-response" value={resp} onChange={(e) => setResp(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                </label>
                <label className="block text-xs text-neutral-600">Next action
                    <input data-testid="contacted-next-action" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                </label>
                <label className="block text-xs text-neutral-600">Next follow-up date
                    <input data-testid="contacted-next-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                </label>
                {["new_lead", "qualified"].includes(stage) && (
                    <label className="flex items-center gap-2 text-xs">
                        <input data-testid="contacted-move-stage" type="checkbox" checked={moveStage} onChange={(e) => setMoveStage(e.target.checked)} />
                        Move stage to Contacted
                    </label>
                )}
                <div className="flex justify-end gap-2 pt-2">
                    <button data-testid="contacted-cancel" onClick={onClose} className="px-4 py-1.5 text-sm rounded-full border">Cancel</button>
                    <button data-testid="contacted-confirm" onClick={submit} className="px-4 py-1.5 text-sm rounded-full bg-cafe-espresso text-white">I sent it — record contact</button>
                </div>
            </div>
        </div>
    );
}

const SUGGESTED = ["Send first outreach", "Follow up on message", "Ask for discovery call", "Follow up on proposal",
    "Confirm budget", "Confirm launch date", "Send revised proposal", "Move to nurture", "Close as lost"];

function FollowupSec({ id, d, c, reload }) {
    const [note, setNote] = useState("");
    const [next, setNext] = useState("");
    const [date, setDate] = useState("");
    const complete = async (snooze = false) => {
        if (!next.trim() || !date) return toast.error("Next action and date are required");
        try {
            await axios.post(`${API}/crm/leads/${id}/followup/complete`, { note, next_action: next, next_date: date, snooze });
            toast.success(snooze ? "Snoozed" : "Follow-up completed"); setNote(""); setNext(""); setDate(""); reload();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-3" data-testid="section-followup">
            <Box title="Current follow-up">
                <div className="text-sm"><b className="text-cafe-olive">→ {c.next_action || "No action set"}</b></div>
                <div className={`text-xs mt-1 ${c.followup_status === "overdue" ? "text-red-600 font-semibold" : "text-neutral-600"}`}>
                    Status: {c.followup_status.replace("_", " ")} · Date: {c.followup_date || "—"}
                </div>
                {d.followup_note && <div className="text-xs text-neutral-500 mt-1">Note: {d.followup_note}</div>}
            </Box>
            <Box title="Complete this follow-up (requires the next step)">
                <label className="block text-xs text-neutral-600">What happened?
                    <input data-testid="followup-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                </label>
                <label className="block text-xs text-neutral-600 mt-2">Next action (required)
                    <input data-testid="followup-next-action" value={next} onChange={(e) => setNext(e.target.value)} list="suggested-actions" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                    <datalist id="suggested-actions">{SUGGESTED.map((s) => <option key={s} value={s} />)}</datalist>
                </label>
                <div className="flex flex-wrap gap-1 mt-1">
                    {SUGGESTED.slice(0, 6).map((s) => <button key={s} onClick={() => setNext(s)} className="text-[10px] px-2 py-0.5 rounded-full bg-neutral-100">{s}</button>)}
                </div>
                <label className="block text-xs text-neutral-600 mt-2">Next date (required)
                    <input data-testid="followup-next-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" />
                </label>
                <div className="flex gap-2 mt-3">
                    <button data-testid="followup-complete-btn" onClick={() => complete(false)} className="text-xs px-4 py-2 rounded-full bg-cafe-espresso text-white">Complete follow-up</button>
                    <button data-testid="followup-snooze-btn" onClick={() => complete(true)} className="text-xs px-4 py-2 rounded-full border">Snooze</button>
                </div>
            </Box>
        </div>
    );
}

function ActivitySec({ d }) {
    const acts = [...(d.activity || [])].reverse();
    const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-MY", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
    return (
        <Box title="Activity timeline (Asia/Kuala_Lumpur)" testid="section-activity">
            {acts.length ? acts.map((a, i) => (
                <div key={i} className="text-xs border-b last:border-0 py-2 flex gap-3">
                    <span className="text-neutral-400 whitespace-nowrap w-36">{fmt(a.at)}</span>
                    <span>
                        <b>{a.action}</b> <span className="text-neutral-400">by {a.actor}</span>
                        {(a.prev || a.new) && <span className="text-neutral-500"> — {a.prev != null ? `${a.prev} → ` : ""}{a.new != null ? `${a.new}` : ""}</span>}
                        {a.source && <span className="block text-neutral-400">{a.source}</span>}
                    </span>
                </div>
            )) : <div className="text-xs text-neutral-400">No activity yet.</div>}
        </Box>
    );
}
